import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ScrollText, CheckCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TermosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  termoTexto: string;
  perfilId: string;
  contato: string;
  token: string;
  onAccepted: () => void;
}

const hashTermo = async (texto: string): Promise<string> => {
  const data = new TextEncoder().encode(texto);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

const TermosDialog = ({ open, onOpenChange, termoTexto, perfilId, contato, token, onAccepted }: TermosDialogProps) => {
  const [accepting, setAccepting] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) {
      setScrolledToEnd(true);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const termoHash = await hashTermo(termoTexto);
      const { error } = await supabase.functions.invoke("accept-terms", {
        body: { contato, token, termo_hash: termoHash },
      });
      if (error) throw error;
      toast.success("Termos aceitos!");
      onAccepted();
    } catch (err: any) {
      toast.error(err.message || "Erro ao aceitar termos");
    }
    setAccepting(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="tibia-border bg-card max-w-lg max-h-[90vh] flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary gold-text-glow text-center flex items-center justify-center gap-2">
            <ScrollText className="w-4 h-4" />
            TERMOS DE USO
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground text-center">
            Leia os termos abaixo e aceite para continuar.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto tibia-border-inner bg-secondary/20 rounded-sm p-4 max-h-[50vh] text-xs text-foreground whitespace-pre-wrap leading-relaxed"
          onScroll={handleScroll}
        >
          {termoTexto}
        </div>

        {!scrolledToEnd && (
          <p className="text-[10px] text-muted-foreground text-center animate-pulse">
            ↓ Role até o final para aceitar ↓
          </p>
        )}

        <motion.button
          whileHover={scrolledToEnd ? { scale: 1.02 } : {}}
          whileTap={scrolledToEnd ? { scale: 0.98 } : {}}
          onClick={handleAccept}
          disabled={!scrolledToEnd || accepting}
          className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {accepting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <CheckCircle className="w-3 h-3" />
              <span className="font-pixel text-[8px]">Li e Aceito os Termos</span>
            </>
          )}
        </motion.button>
      </DialogContent>
    </Dialog>
  );
};

export { hashTermo };
export default TermosDialog;
